import React from 'react';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 90px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 56px;

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;
    margin-bottom: 14px;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .desc {
    font-size: 14px;
    color: #5b6b7c;
    line-height: 1.7;
    max-width: 520px;
    margin: 0 auto;
  }
`;

const LogoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 48px 72px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 36px 48px;
  }

  @media (max-width: 480px) {
    gap: 28px 36px;
  }
`;

const LogoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90px;
  transition: transform 0.4s ease;

  @media (max-width: 768px) {
    height: 70px;
  }

  img {
    max-width: 240px;
    max-height: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease;

    @media (max-width: 768px) {
      max-width: 170px;
    }
  }

  &:hover {
    transform: scale(1.05);
  }
`;

/* A single upload is a combined artwork of every partner, so it gets the full
   width rather than being squeezed into one logo slot. */
const CombinedLogo = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

// Sanity's CDN doesn't transform SVGs, so leave those URLs untouched.
const sized = (url, width) =>
  url.endsWith('.svg') ? url : `${url}?w=${width}&auto=format`;

export default function Partners({ tag, title, description, logos }) {
  const data = logos || [];
  const isCombined = data.length === 1;

  if (!data.length && !title) return null;

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          <Branded as="h2" text={title} />
          <Branded as="p" className="desc" text={description} />
        </Header>

        {isCombined && data[0]?.asset?.url && (
          <CombinedLogo>
            <img
              src={sized(data[0].asset.url, 1600)}
              alt="Our partners"
              loading="lazy"
            />
          </CombinedLogo>
        )}

        {!isCombined && data.length > 0 && (
          <LogoGrid>
            {data.map((logo, idx) => (
              <LogoItem key={idx}>
                {logo?.asset?.url && (
                  <img
                    src={sized(logo.asset.url, 300)}
                    alt={`Partner ${idx + 1}`}
                    loading="lazy"
                  />
                )}
              </LogoItem>
            ))}
          </LogoGrid>
        )}
      </Container>
    </Section>
  );
}
